import React, { useEffect } from "react";
import {
  addNewDoc,
  deleteDocById,
  setDocData,
} from "../../../utils/firebase/Firebase.utils";
import InputBox from "../Components/InputBox";
import { useSelector } from "react-redux";
import PhotosUploader from "./PhotosUploader";
import { handleUploadMultiple } from "../../../utils/EleghoUtils";
import toast from "react-hot-toast";
import FacilityCard from "../../User/Facilities/FacilityCard";
import {
  FaTimes,
  FaEdit,
  FaTrash,
  FaPlus,
  FaMinus,
  FaUpload,
  FaCheck,
  FaSpinner,
} from "react-icons/fa";
import { MdFeaturedPlayList, MdOutlineAddPhotoAlternate } from "react-icons/md";

export default function NewFacilityForm({ data, setAddMode, setData }) {
  const currentUser = useSelector((state) => state.userObject);
  const [formData, setFormData] = React.useState(data || {});
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [editMode, setEditMode] = React.useState(data ? false : true);

  const [keyValuePairArray, setKeyValuePairArray] = React.useState(
    data
      ? Object.entries(data.Features).map(([key, value]) => ({ key, value }))
      : [{ key: "", value: "" }] || [{ key: "", value: "" }]
  );
  const [featuresObject, setFeaturesObject] = React.useState(
    data?.Features || {}
  );
  const [roomFeatures, setRoomFeatures] = React.useState(
    data?.roomFeatures || [""]
  );
  const [photos, setPhotos] = React.useState(data ? data.photos : []);
  const [primaryPhoto, setPrimaryPhoto] = React.useState(
    data?.primaryPhoto || ""
  );

  useEffect(() => {
    setFormData({ ...formData, photos });
  }, [photos]);

  useEffect(() => {
    const newObject = keyValuePairArray.reduce((acc, item) => {
      // remove empty key value pairs
      if (item.key && item.value) {
        acc[item.key] = item.value;
        return acc;
      } else {
        return acc;
      }
    }, {});

    setFeaturesObject(newObject);
  }, [keyValuePairArray]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleKeyValuePairChange = (e, index, key) => {
    const newArray = [...keyValuePairArray];
    newArray[index][key] = e.target.value;
    setKeyValuePairArray(newArray);
  };

  const handleAddToObject = () => {
    setKeyValuePairArray([...keyValuePairArray, { key: "", value: "" }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    const data = {
      ...formData,
      photos: photos,
      primaryPhoto: primaryPhoto || photos[0] || "",
      Features: featuresObject,
      roomFeatures: roomFeatures,
      createdBy: currentUser.uid,
      dateCreated: new Date().getTime(),
    };

    if (formData.id) {
      await setDocData("facilities", formData.id, data);
      setIsProcessing(false);
      setAddMode(false);
      setData(null);
      toast.success("Facility Updated");
      return;
    }

    await addNewDoc("facilities", data);
    setIsProcessing(false);
    setAddMode(false);
    setData(null);
    toast.success("Facility Added");
  };

  const handleUpload = async (e) => {
    await handleUploadMultiple(
      e,
      formData,
      setFormData,
      `facilities`,
      "photos"
    );
    setPhotos(formData.photos);
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this facility?")) {
      await deleteDocById("facilities", formData.id);
      toast.success("Facility Deleted");
      setData(null);
      setAddMode(false);
    }
  };

  const handleEditMode = () => {
    setEditMode(!editMode);
  };

  return (
    <div className="page relative text-left bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-[#363062]">
          {data ? "Edit Facility" : "Create New Facility"}
        </h2>
        <button
          className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-all"
          onClick={() => {
            setAddMode(false);
            setData(null);
          }}
        >
          <FaTimes />
        </button>
      </div>

      {data && (
        <div className="flex items-center gap-4 mb-6">
          <button
            type="button"
            className="flex items-center gap-1 text-[#363062] hover:underline text-sm"
            onClick={handleEditMode}
          >
            {editMode ? (
              <>
                <FaTimes className="text-xs" /> Cancel
              </>
            ) : (
              <>
                <FaEdit className="text-xs" /> Edit
              </>
            )}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 text-red-500 hover:underline text-sm"
            onClick={handleDelete}
          >
            <FaTrash className="text-xs" /> Delete
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-8">
        {/* Form Section */}
        <div className="w-full md:w-5/12 min-w-[320px]">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
              <InputBox
                name="name"
                label="Facility Name"
                type="text"
                placeholder={"Enter Facility Name"}
                value={formData.name}
                onChange={handleChange}
                required
                disabled={!editMode}
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
              <InputBox
                name="description"
                label="Description"
                type="text"
                placeholder={"Enter Description"}
                value={formData.description}
                onChange={handleChange}
                disabled={!editMode}
                required
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <MdFeaturedPlayList className="text-[#363062]" />
                <h3 className="font-medium text-[#363062]">
                  Technical Features
                </h3>
              </div>

              {editMode && (
                <button
                  onClick={handleAddToObject}
                  type="button"
                  className="flex items-center gap-1 bg-[#8FCD00] text-white px-3 py-1.5 rounded-md text-sm hover:bg-opacity-90 transition-all mb-4"
                >
                  <FaPlus className="text-xs" /> Add Feature
                </button>
              )}

              <div className="space-y-3">
                {keyValuePairArray.map((item, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <InputBox
                        label="Feature Name"
                        type="text"
                        placeholder="Feature Name"
                        disabled={!editMode}
                        value={item.key}
                        onChange={(e) =>
                          handleKeyValuePairChange(e, index, "key")
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <InputBox
                        label="Value"
                        type="text"
                        placeholder="Value"
                        value={item.value}
                        disabled={!editMode}
                        onChange={(e) =>
                          handleKeyValuePairChange(e, index, "value")
                        }
                      />
                    </div>
                    {editMode && (
                      <button
                        className="text-red-500 p-2 rounded-full hover:bg-red-50"
                        type="button"
                        onClick={() => {
                          const newArray = [...keyValuePairArray];
                          newArray.splice(index, 1);
                          setKeyValuePairArray(newArray);
                        }}
                      >
                        <FaMinus className="text-xs" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <MdFeaturedPlayList className="text-[#363062]" />
                <h3 className="font-medium text-[#363062]">Key Features</h3>
              </div>

              {editMode && (
                <button
                  className="flex items-center gap-1 bg-[#8FCD00] text-white px-3 py-1.5 rounded-md text-sm hover:bg-opacity-90 transition-all mb-4"
                  type="button"
                  onClick={() => {
                    setRoomFeatures([...roomFeatures, ""]);
                  }}
                >
                  <FaPlus className="text-xs" /> Add Feature
                </button>
              )}

              <div className="space-y-2">
                {roomFeatures.map((item, index) => (
                  <div className="flex items-center gap-2" key={index}>
                    <div className="w-2 h-2 rounded-full bg-[#8FCD00]"></div>
                    <div className="flex-1">
                      <InputBox
                        type="text"
                        value={item}
                        disabled={!editMode}
                        placeholder="e.g. 24/7 Security"
                        onChange={(e) => {
                          const newArray = [...roomFeatures];
                          newArray[index] = e.target.value;
                          setRoomFeatures(newArray);
                        }}
                      />
                    </div>
                    {editMode && (
                      <button
                        className="text-red-500 p-2 rounded-full hover:bg-red-50"
                        type="button"
                        onClick={() => {
                          const newArray = [...roomFeatures];
                          newArray.splice(index, 1);
                          setRoomFeatures(newArray);
                        }}
                      >
                        <FaMinus className="text-xs" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <MdOutlineAddPhotoAlternate className="text-[#363062]" />
                <h3 className="font-medium text-[#363062]">Photos</h3>
              </div>
              <PhotosUploader
                data={photos}
                setData={setPhotos}
                primaryPhoto={primaryPhoto}
                setPrimaryPhoto={setPrimaryPhoto}
                uploader={handleUpload}
                disabled={!editMode}
              />
            </div>

            {editMode && (
              <button
                className="flex items-center justify-center gap-2 bg-[#363062] text-white py-2.5 px-4 rounded-md hover:bg-opacity-90 transition-all mt-2"
                type="submit"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <FaSpinner className="animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    <FaCheck /> Submit
                  </>
                )}
              </button>
            )}
          </form>
        </div>

        {/* Preview Section */}
        <div className="w-full md:w-5/12 min-w-[320px]">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-4 text-[#363062]">
              <FaCheck className="text-[#8FCD00]" />
              <h3 className="font-medium">Preview</h3>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <FacilityCard data={formData} primaryPhoto={primaryPhoto} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
